import 'moment/locale/en-ca';
import 'moment/locale/fr';

import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule, NativeDateAdapter } from '@angular/material/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Injectable, NgModule } from '@angular/core';
import { CommonModule as AngularCommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { ImageCropperModule } from 'ngx-image-cropper';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { RouterModule } from '@angular/router';
import { TextMaskModule } from 'angular2-text-mask';
import { TranslateModule, TranslateStore } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxBarcode6Module } from 'ngx-barcode6';
import { NgxQRCodeModule } from '@techiediaries/ngx-qrcode';
import { QuillModule } from 'ngx-quill';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CdkColumnDef } from '@angular/cdk/table';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { NgxMatDateFormats, NgxMatDatetimePickerModule, NgxMatDateAdapter, NGX_MAT_DATE_FORMATS } from '@angular-material-components/datetime-picker';
import { NgxMatMomentAdapter, NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular-material-components/moment-adapter';
import { MAT_COLOR_FORMATS, NGX_MAT_COLOR_FORMATS, NgxMatColorPickerModule } from '@angular-material-components/color-picker';
import { ColorPickerModule } from 'ngx-color-picker';
import { PortalModule } from '@angular/cdk/portal';
import { OverlayModule } from '@angular/cdk/overlay';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';

import { AddressAutocompleteComponent } from './components/forms/address-autocomplete/address-autocomplete.component';
import { AuthModule } from '../auth/auth.module';
import { BcitiTabComponent } from './components/common/tabs/tab/tab.component';
import { BcitiTabsComponent } from './components/common/tabs/tabs.component';
import { ChangePasswordComponent } from './pages/change-password/change-password.component';
import { CompleteProfileDialogComponent } from './components/dialogs/complete-account-dialog/complete-profile-dialog.component';
import { ConfigAllComponent } from './pages/config-all/config-all.component';
import { ConfirmAccountComponent } from './pages/confirm-account/confirm-account.component';
import { DialogTitleComponent } from './components/dialogs/dialog-title/dialog-title.component';
import { DocumentViewerComponent } from './components/common/document-viewer/modal-image.component';
import { ExternalSystemsComponent } from './pages/external-systems/external-systems.component';
import { FormCheckboxComponent } from './components/forms/checkbox/checkbox.component';
import { FormToggleComponent } from './components/forms/toggle/toggle.component';
import { FormDatepickerComponent } from './components/forms/datepicker/datepicker.component';
import { FormErrorComponent } from './components/forms/error/error.component';
import { FormInputComponent } from './components/forms/input/input.component';
import { FormInputV2Component } from './components/forms/input-v2/input-v2.component';
import { FormRadioComponent } from './components/forms/radio/radio.component';
import { GeoidAddressComponent } from './components/forms/geoid-address/geoid-address.component';
import { GeoidAddressModalComponent } from './components/forms/geoid-address/modal/geoid-address-modal.component';
import { HomeComponent } from './pages/home/home.component';
import { SecondaryNavTitlePipe } from './pipes/secondary-nav-title/secondary-nav-title.pipe';
import { IFRAME_MODULE_SERVICE_PROVIDER } from './services/iframe-module/iframe-module.service';
import { IframePageComponent } from './pages/iframe/iframe.component';
import { InformationalDialogComponent } from './components/dialogs/informational-dialog/informational-dialog.component';
import { InformationalDialogV2Component } from './components/dialogs/informational-dialog-v2/informational-dialog-v2.component';
import { LayoutClassicComponent } from './layouts/classic/classic.component';
import { LayoutFloatingCenterComponent } from './layouts/floating-center/floating-center.component';
import { LoginComponent } from './pages/login/login.component';
import { LogoutComponent } from './pages/logout/logout.component';
import { CallbackComponent } from './pages/callback/callback.component';
import { MESSAGE_SERVICE_PROVIDER } from './services/message/message.service';
import { MaterialModule } from '../material.module';
import { MenuComponent } from './components/menu/menu.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { ORGANISATION_SERVICE_PROVIDER } from './services/organisation/organisation.service';
import { BcitiContextServiceImpl } from './services/context/context.service';
import { ProfilePictureComponent } from './components/forms/profile-picture/profile-picture.component';
import { PhoneMaskDirective } from './directives/phone-mask/phone-mask.directive';
import { ProfilePictureUploadComponent } from './components/forms/profile-picture-upload/profile-picture-upload.component';
import { ResetPasswordComponent } from './pages/reset-password/reset-password.component';
import { ResizeProfilePictureModalComponent } from './components/forms/resize-profile-picture-modal/resize-profile-picture-modal.component';
import { SafeUrlPipe } from './pipes/safeUrl/safe-url.pipe';
import { SelectComponent } from './components/forms/select/select.component';
import { SelectDropdownComponent } from './components/forms/select/dropdown/dropdown.component';
import { SelectionListComponent } from './components/forms/selection-list/selection-list.component';
import { ToolbarComponent } from './components/toolbar/toolbar.component';
import { UserRoleModalComponent } from '../user/components/user-role/user-role-modal.component';
import { ConfirmDialogComponent } from './components/dialogs/confirm-dialog/confirm-dialog.component';
import { ConfirmDialogV2Component } from './components/dialogs/confirm-dialog-v2/confirm-dialog-v2.component';
import { LocalizedDatePipe } from './pipes/localizedDate/localized-date.pipe';
import { DatePipe } from './pipes/date/date.pipe';
import { LocalizeCurrencyPipe } from './pipes/localizeCurrency/localizeCurrency.pipe';
import { BcitiTableComponent } from './components/common/table/bciti-table.component';
import { SafeHtmlPipe } from './pipes/safeHtml/safeHtml.pipe';
import { CollectionsTableComponent } from './components/collections/table/collections-table.component';
import { CollectionsDashboardComponent } from './components/collections/collections-dashboard/collections-dashboard.component';
import { CollectionsAnalyticsComponent } from './components/collections/collections-analytics/collections-analytics.component';
import { CollectsKpiCardsComponent } from './components/collections/collections-analytics/collects-kpi-cards/collects-kpi-cards.component';
import { CollectsEventTypeChartComponent } from './components/collections/collections-analytics/collects-event-type-chart/collects-event-type-chart.component';
import { CollectsAreaChartComponent } from './components/collections/collections-analytics/collects-area-chart/collects-area-chart.component';
import { CollectsLanguageChartComponent } from './components/collections/collections-analytics/collects-language-chart/collects-language-chart.component';
import { CollectsViewsTrendComponent } from './components/collections/collections-analytics/collects-views-trend/collects-views-trend.component';
import { CollectsDownloadsTrendComponent } from './components/collections/collections-analytics/collects-downloads-trend/collects-downloads-trend.component';
import { CollectsHeatmapComponent } from './components/collections/collections-analytics/collects-heatmap/collects-heatmap.component';
import { CollectsAreaUsersChartComponent } from './components/collections/collections-analytics/collects-area-users-chart/collects-area-users-chart.component';
import { CollectionsConfigComponent } from './components/collections/collections-config/collections-config.component';
import { COLLECTS_ANALYTICS_SERVICE_PROVIDER } from './services/collects-analytics/collects-analytics.impl.service';
import { CollectsReminderAnalyticsComponent } from './components/collections/collections-analytics/collects-reminder-analytics/collects-reminder-analytics.component';
import { ReminderKpiCardsComponent } from './components/collections/collections-analytics/collects-reminder-analytics/reminder-kpi-cards/reminder-kpi-cards.component';
import { ReminderChannelChartComponent } from './components/collections/collections-analytics/collects-reminder-analytics/reminder-channel-chart/reminder-channel-chart.component';
import { ReminderStatusChartComponent } from './components/collections/collections-analytics/collects-reminder-analytics/reminder-status-chart/reminder-status-chart.component';
import { ReminderCollectionTypeChartComponent } from './components/collections/collections-analytics/collects-reminder-analytics/reminder-collection-type-chart/reminder-collection-type-chart.component';
import { ReminderAreaChartComponent } from './components/collections/collections-analytics/collects-reminder-analytics/reminder-area-chart/reminder-area-chart.component';
import { ReminderVolumeTrendComponent } from './components/collections/collections-analytics/collects-reminder-analytics/reminder-volume-trend/reminder-volume-trend.component';
import { ReminderDeliveryRateTrendComponent } from './components/collections/collections-analytics/collects-reminder-analytics/reminder-delivery-rate-trend/reminder-delivery-rate-trend.component';
import { REMINDER_ANALYTICS_SERVICE_PROVIDER } from './services/reminder-analytics/reminder-analytics.impl.service';
import { BcitiCategoryInputComponent } from './components/forms/category/bciti-category-input.component';
import { BcitiColorSelectComponent } from './components/forms/color-select/bciti-color-select.component';
import { BcitiIconSelectComponent } from './components/forms/icon-select/bciti-icon-select.component';
import { BcitiShapeSelectComponent } from './components/forms/shape-select/bciti-shape-select.component';
import { BcitiSelectGridDropdownComponent } from './components/forms/select-grid-dropdown/bciti-select-grid-dropdown.component';
import { CollectionsListComponent } from './pages/collections/collections-list/collections-list.component';
import { CollectionScheduleComponent } from './components/collections/collection-schedule/collection-schedule.component';
import { CollectionConfigurationComponent } from './pages/collections/collection-configuration/collection-configuration.component';
import { BcitiTextSelectComponent } from './components/forms/text-select/bciti-text-select.component';
import { ChipListComponent } from './components/forms/chip-list/chip-list.component';
import { BcitiSelectListDropdownComponent } from './components/forms/select-list-dropdown/bciti-select-list-dropdown.component';
import { FormSelectMultipleComponent } from './components/forms/select-multiple/select-multiple.component';
import { SelectMultipleDropdownComponent } from './components/forms/select-multiple/dropdown/selectDropdown.component';
import { FormSuccessComponent } from './components/forms/success/success.component';
import { BcitiTimeInputComponent } from './components/forms/time-input/bciti-time-input.component';
import { ServiceDetailDialogComponent } from './components/dialogs/service-detail-dialog/service-detail-dialog.component';
import { ServiceStateConfigurationComponent } from './pages/services-state/service-state-configuration/service-state-configuration.component';
import { ServiceStateEditComponent } from './pages/services-state/service-state-edit/service-state-edit.component';
import { ServiceStateFormComponent } from './components/service-state/service-state-form/service-state-form.component';
import { ServiceScheduleDialogComponent } from './components/dialogs/service-schedule-dialog/service-schedule-dialog.component';
import { FormTextareaComponent } from './components/forms/textarea/textarea.component';
import { ServiceStateScheduleIntervalComponent } from './components/service-state/service-state-schedule-interval/service-state-schedule-interval.component';
import { ServicesCategoriesDialogComponent } from './components/dialogs/services-categories-dialog/services-categories-dialog.component';
import { ServicesStateListComponent } from './pages/services-state/services-state-list.component';
import { ServiceStateTableComponent } from './components/service-state/service-state-table/service-state-table.component';
import { ConfigCardsComponent } from './components/config-all/config-cards/config-cards.component';
import { CONFIG_ALL_SERVICE_PROVIDER } from './services/config-all/config-all.impl.service';
import { ConfigPrintCardsComponent } from './components/config-all/config-cards/components/config-print-cards/config-print-cards.component';
import { ShortDatePipe } from './pipes/shortDate/short-date.pipe';
import { ConfigCardsSelectComponent } from './components/config-all/config-cards/components/config-cards-select/config-cards-select.component';
import { ConfigCardsSelectDropdownComponent } from './components/config-all/config-cards/components/config-cards-select-dropdown/config-cards-select-dropdown.component';
import { ConfigContactInfoComponent } from './components/config-all/config-contact-info/config-contact-info.component';
import { ConfigRequestsComponent } from './components/config-all/config-requests/config-requests.component';
import { ConfigFamilyComponent } from './components/config-all/config-family/config-family.component';
import { ConfigResidenceComponent } from './components/config-all/config-residence/config-residence.component';
import { DenialReasonsTableComponent } from './components/config-all/denial-reasons-table/denial-reasons-table.component';
import { DenialReasonDialogComponent } from './components/dialogs/denial-reason-dialog/denial-reason-dialog.component';
import { ReportProblemDialogComponent } from './components/dialogs/report-problem-dialog/report-problem-dialog.component';
import { ReportAttachmentsComponent } from './components/dialogs/report-problem-dialog/report-attachments/report-attachments.component';
import { PredefinedMessageDialogComponent } from './components/dialogs/predefined-message-dialog/predefined-message-dialog.component';
import { ServiceAddCtaDialogComponent } from './components/dialogs/service-add-cta-dialog/service-add-cta-dialog.component';
import { ServicesStatusesDialogComponent } from './components/dialogs/services-statuses-dialog/services-statuses-dialog.component';
import { SERVICES_SERVICE_PROVIDER } from './services/service-state/service-state.impl.service';
import { ServiceManualStatusChangeDialogComponent } from './components/dialogs/service-manual-status-change-dialog/service-manual-status-change-dialog.component';
import { TimeFormatPipe } from './pipes/localizedTimeFormat/time-format.pipe';
import { CollectionAreasDialogComponent } from './components/dialogs/collection-areas-dialog/collection-areas-dialog.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { ExportsComponent } from './pages/exports/exports.component';
import { REPORTS_SERVICE_PROVIDER_IMPL } from './services/reports/reports.service';
import { COLLECTIONS_SERVICE_PROVIDER } from './services/collections/collections.service';
import { CollectionFormComponent } from './components/collections/collection-form/collection-form.component';
import { CollectionEditComponent } from './pages/collections/collection-edit/collection-edit.component';
import { RequestDetailComponent } from './components/requests/detail/request-detail.component';
import { REQUESTS_SERVICE_PROVIDER } from './services/requests/requests.impl.service';
import { BcitiConfirmDialogComponent } from './components/dialogs/bciti-confirm-dialog/bciti-confirm-dialog.component';
import { ExternalSystemsSelectDropdownComponent } from './components/external-systems/dropdown/external-systems-select-dropdown.component';
import { ExternalSystemsSelectComponent } from './components/external-systems/select/external-systems-select.component';
import { EXTERNAL_SYSTEMS_SERVICE_PROVIDER } from './services/external-systems/external-systems.impl.service';
import { GEOID_SERVICE_PROVIDER } from './components/forms/geoid-address/service/geoid.service';
import { UserCardFormSelectComponent } from './components/forms/select-user-cards/select-user-cards.component';
import { BottomSheetComponent } from './components/common/bottom-sheet/bottom-sheet.component';
import { UserCardSelectDropdownNotificationComponent } from './components/forms/select-user-cards/dropdown/select-user-cards-dropdown.component';
import { AvatarComponent } from './components/common/avatar/avatar.component';
import { TwoDecimalPipe } from './pipes/two-decimal/two-decimal.pipe';
import { LocalizeDatePipe } from './pipes/localizeDate/localizeDate.pipe';
import { ModalImageComponent } from './components/common/modal-image/modal-image.component';
import { REPORT_PROBLEM_SERVICE_PROVIDER } from './services/report-problem/report-problem.impl.service';
import { UserDashboardComponent } from './components/user/user-dashboard/user-dashboard.component';
import { UserAnalyticsComponent } from './components/user/user-analytics/user-analytics.component';
import { UserConfigComponent } from './components/user/user-config/user-config.component';
import { UserKpiCardsComponent } from './components/user/user-analytics/user-kpi-cards/user-kpi-cards.component';
import { UserCardTypeChartComponent } from './components/user/user-analytics/user-card-type-chart/user-card-type-chart.component';
import { UserSubscriptionCodeChartComponent } from './components/user/user-analytics/user-subscription-code-chart/user-subscription-code-chart.component';
import { UserNewUsersTrendComponent } from './components/user/user-analytics/user-new-users-trend/user-new-users-trend.component';
import { UserCardRegistrationsTrendComponent } from './components/user/user-analytics/user-card-registrations-trend/user-card-registrations-trend.component';
import { UserSubscriptionsTrendComponent } from './components/user/user-analytics/user-subscriptions-trend/user-subscriptions-trend.component';
import { UserHeatmapComponent } from './components/user/user-analytics/user-heatmap/user-heatmap.component';
import { USER_ANALYTICS_SERVICE_PROVIDER } from './services/user-analytics/user-analytics.impl.service';
import { NgChartsModule } from 'ng2-charts';
import { ConfirmFormInputComponent } from './components/forms/confirm-input/confirm-input.component';
import { ColorPickerComponent } from './components/color-picker/color-picker.component';
import { ConfigMenuColorComponent } from './components/config-all/config-menu-color/config-menu-color.component';
import { StatusMigrationDialogComponent } from '../request-v2/components/request-settings-dialog/components/status-migration-dialog/status-migration-dialog.component';
import { SearchableDropdownModule } from './components/searchable-dropdown/searchable-dropdown.module';
import { SelectV2Component } from './components/forms/select-v2/select.component';
import { SelectDropdownV2Component } from './components/forms/select-v2/dropdown/dropdown.component';
import { AccessControlConfigurationComponent } from './pages/access-control/access-control-configuration/access-control-configuration.component';
import { AccessControlEditComponent } from './pages/access-control/access-control-edit/access-control-edit.component';
import { AccessControlListComponent } from './pages/access-control/access-control-list/access-control-list.component';
import { AccessControlFormComponent } from './components/access-control/access-control-form/access-control-form.component';
import { AccessControlTableComponent } from './components/access-control/access-control-table/access-control-table.component';
import { ToggleSwitchComponent } from './components/common/toggle-switch/toggle-switch.component';
import { AccessControlBannerComponent } from './components/access-control/access-control-banner/access-control-banner.component';
import { SearchDropdownModule } from './components/common/search-dropdown/search-dropdown.module';
import { AccessControlRoleDialogComponent } from './components/dialogs/access-control-role-dialog/access-control-role-dialog.component';
import { ACCESS_SERVICE_PROVIDER } from './services/access-control/access-control.service';
import { TransferDialogComponent } from './components/dialogs/transfer-dialog/transfer-dialog.component';
import { RoleSelectorComponent } from './components/common/role-selector/role-selector.component';
import { TableFilterComponent } from './components/common/table-filter/table-filter.component';
import { UserChipComponent } from './components/common/user-chip/user-chip.component';
import { RequestComponent } from './pages/requests/requests.component';

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
    /**
     * Parses a date string into a Date object.
     *
     * Overrides the default behavior to handle ISO date-only strings (YYYY-MM-DD)
     * as local time instead of UTC. This prevents the "day slip" bug when users
     * paste dates like "1975-03-16" into the datepicker.
     *
     * JavaScript quirk: new Date('1975-03-16') = UTC midnight
     *                   new Date('1975-03-16T00:00:00') = local midnight
     *                   new Date('1975/03/16') = local midnight
     *
     * @param value - The date string to parse
     * @returns A Date object in local time, or null if invalid
     */
    override parse(value: any): Date | null {
        if (typeof value === 'string' && value) {
            // Check if it's an ISO date-only format (YYYY-MM-DD)
            // This format is problematic because JS interprets it as UTC
            const isoDateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (isoDateOnlyRegex.test(value.trim())) {
                // Append T00:00:00 to force local time interpretation
                const [year, month, day] = value.trim().split('-').map(Number);
                // Month is 0-indexed in JavaScript Date
                return new Date(year, month - 1, day);
            }
        }
        // For all other formats, use the default parsing
        return super.parse(value);
    }

    override format(date: Date, _displayFormat: any): string {
        return new Intl.DateTimeFormat('en-ca', {}).format(new Date(date)).replace(/-/g, '/');
    }
}

const CUSTOM_DATE_FORMATS: NgxMatDateFormats = {
    parse: {
        dateInput: 'YYYY/MM/DD HH:mm:ss',
    },
    display: {
        dateInput: 'YYYY/MM/DD HH:mm:ss',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'YYYY/MM/DD HH:mm:ss',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@NgModule({
    imports: [
        TranslateModule,
        BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        AngularCommonModule,
        MatListModule,
        MatDividerModule,
        MatDatepickerModule,
        MatNativeDateModule,
        RouterModule,
        AuthModule,
        ImageCropperModule,
        MaterialModule,
        MatMomentDateModule,
        TextMaskModule,
        MatProgressSpinnerModule,
        NgxQRCodeModule,
        NgxBarcode6Module,
        QuillModule.forRoot(),
        NgbModule,
        NgxChartsModule,
        NgxJsonViewerModule,
        NgxMatDatetimePickerModule,
        NgxMatColorPickerModule,
        ColorPickerModule,
        SearchableDropdownModule,
        SearchDropdownModule,
        OverlayModule,
        PortalModule,
        NgxExtendedPdfViewerModule,
        NgChartsModule,
    ],
    declarations: [
        BcitiConfirmDialogComponent,
        LayoutClassicComponent,
        LayoutFloatingCenterComponent,
        ToolbarComponent,
        MenuComponent,
        HomeComponent,
        NotFoundComponent,
        SecondaryNavTitlePipe,
        LoginComponent,
        LogoutComponent,
        CallbackComponent,
        ResetPasswordComponent,
        ChangePasswordComponent,
        ConfirmAccountComponent,
        ConfigAllComponent,
        ExternalSystemsComponent,
        FormInputComponent,
        FormInputV2Component,
        FormDatepickerComponent,
        FormErrorComponent,
        FormCheckboxComponent,
        FormToggleComponent,
        FormRadioComponent,
        PhoneMaskDirective,
        ProfilePictureComponent,
        ProfilePictureUploadComponent,
        GeoidAddressComponent,
        GeoidAddressModalComponent,
        UserRoleModalComponent,
        SelectionListComponent,
        ResizeProfilePictureModalComponent,
        DocumentViewerComponent,
        BcitiTabsComponent,
        BcitiTabComponent,
        ConfirmDialogComponent,
        ConfirmDialogV2Component,
        SelectComponent,
        SelectDropdownComponent,
        SelectV2Component,
        SelectDropdownV2Component,
        IframePageComponent,
        SafeUrlPipe,
        InformationalDialogComponent,
        InformationalDialogV2Component,
        DialogTitleComponent,
        CompleteProfileDialogComponent,
        AddressAutocompleteComponent,
        LocalizedDatePipe,
        BcitiTableComponent,
        SafeHtmlPipe,
        CollectionsTableComponent,
        CollectionsDashboardComponent,
        CollectionsAnalyticsComponent,
        CollectsKpiCardsComponent,
        CollectsEventTypeChartComponent,
        CollectsAreaChartComponent,
        CollectsLanguageChartComponent,
        CollectsViewsTrendComponent,
        CollectsDownloadsTrendComponent,
        CollectsHeatmapComponent,
        CollectsAreaUsersChartComponent,
        CollectsReminderAnalyticsComponent,
        ReminderKpiCardsComponent,
        ReminderChannelChartComponent,
        ReminderStatusChartComponent,
        ReminderCollectionTypeChartComponent,
        ReminderAreaChartComponent,
        ReminderVolumeTrendComponent,
        ReminderDeliveryRateTrendComponent,
        CollectionsConfigComponent,
        BcitiCategoryInputComponent,
        BcitiColorSelectComponent,
        BcitiIconSelectComponent,
        BcitiShapeSelectComponent,
        BcitiSelectGridDropdownComponent,
        CollectionsListComponent,
        CollectionConfigurationComponent,
        CollectionScheduleComponent,
        BcitiTextSelectComponent,
        ChipListComponent,
        BcitiSelectListDropdownComponent,
        FormSelectMultipleComponent,
        SelectMultipleDropdownComponent,
        FormSuccessComponent,
        BcitiTimeInputComponent,
        FormTextareaComponent,
        ColorPickerComponent,
        ConfigCardsComponent,
        ConfigPrintCardsComponent,
        ConfigMenuColorComponent,
        ShortDatePipe,
        ConfigCardsSelectComponent,
        ConfigCardsSelectDropdownComponent,
        ConfigContactInfoComponent,
        ConfigRequestsComponent,
        ConfigFamilyComponent,
        ConfigResidenceComponent,
        ServiceAddCtaDialogComponent,
        ServiceDetailDialogComponent,
        ServiceStateConfigurationComponent,
        ServiceStateEditComponent,
        ServiceStateFormComponent,
        ServiceScheduleDialogComponent,
        ServiceStateScheduleIntervalComponent,
        ServicesStateListComponent,
        ServiceStateTableComponent,
        ServicesStatusesDialogComponent,
        ServicesCategoriesDialogComponent,
        ServiceManualStatusChangeDialogComponent,
        TimeFormatPipe,
        CollectionAreasDialogComponent,
        DashboardComponent,
        ExportsComponent,
        CollectionFormComponent,
        CollectionEditComponent,
        RequestComponent,
        RequestDetailComponent,
        UserDashboardComponent,
        UserAnalyticsComponent,
        UserConfigComponent,
        UserKpiCardsComponent,
        UserCardTypeChartComponent,
        UserSubscriptionCodeChartComponent,
        UserNewUsersTrendComponent,
        UserCardRegistrationsTrendComponent,
        UserSubscriptionsTrendComponent,
        UserHeatmapComponent,
        ExternalSystemsSelectComponent,
        ExternalSystemsSelectDropdownComponent,
        AvatarComponent,
        UserCardFormSelectComponent,
        BottomSheetComponent,
        AvatarComponent,
        TwoDecimalPipe,
        UserCardSelectDropdownNotificationComponent,
        DatePipe,
        LocalizeCurrencyPipe,
        LocalizeDatePipe,
        ModalImageComponent,
        ConfirmFormInputComponent,
        StatusMigrationDialogComponent,
        AccessControlConfigurationComponent,
        AccessControlEditComponent,
        AccessControlListComponent,
        AccessControlFormComponent,
        AccessControlTableComponent,
        ToggleSwitchComponent,
        AccessControlBannerComponent,
        AccessControlRoleDialogComponent,
        TransferDialogComponent,
        RoleSelectorComponent,
        TableFilterComponent,
        UserChipComponent,
        DenialReasonsTableComponent,
        DenialReasonDialogComponent,
        ReportProblemDialogComponent,
        ReportAttachmentsComponent,
        PredefinedMessageDialogComponent,
    ],
    exports: [
        ToolbarComponent,
        MenuComponent,
        SelectComponent,
        HomeComponent,
        LayoutClassicComponent,
        LayoutFloatingCenterComponent,
        NotFoundComponent,
        LoginComponent,
        CallbackComponent,
        ConfigAllComponent,
        SecondaryNavTitlePipe,
        FormTextareaComponent,
        FormErrorComponent,
        FormInputComponent,
        FormInputV2Component,
        FormDatepickerComponent,
        FormErrorComponent,
        FormCheckboxComponent,
        FormToggleComponent,
        FormRadioComponent,
        BcitiIconSelectComponent,
        PhoneMaskDirective,
        ProfilePictureComponent,
        ProfilePictureUploadComponent,
        GeoidAddressComponent,
        GeoidAddressModalComponent,
        UserRoleModalComponent,
        SelectionListComponent,
        ResizeProfilePictureModalComponent,
        DocumentViewerComponent,
        BcitiTabComponent,
        BcitiTabsComponent,
        IframePageComponent,
        LocalizedDatePipe,
        BcitiCategoryInputComponent,
        InformationalDialogComponent,
        InformationalDialogV2Component,
        SafeHtmlPipe,
        NgxBarcode6Module,
        NgxQRCodeModule,
        AvatarComponent,
        MatListModule,
        UserCardFormSelectComponent,
        BottomSheetComponent,
        AvatarComponent,
        ShortDatePipe,
        TwoDecimalPipe,
        UserCardSelectDropdownNotificationComponent,
        LocalizeCurrencyPipe,
        SafeHtmlPipe,
        LocalizeDatePipe,
        ModalImageComponent,
        BcitiTableComponent,
        SelectDropdownComponent,
        DialogTitleComponent,
        ConfirmFormInputComponent,
        NgxMatColorPickerModule,
        ColorPickerModule,
        StatusMigrationDialogComponent,
        TranslateModule,
        SelectV2Component,
        SearchDropdownModule,
    ],
    providers: [
        COLLECTIONS_SERVICE_PROVIDER,
        SERVICES_SERVICE_PROVIDER,
        REQUESTS_SERVICE_PROVIDER,
        ACCESS_SERVICE_PROVIDER,
        ORGANISATION_SERVICE_PROVIDER,
        MESSAGE_SERVICE_PROVIDER,
        IFRAME_MODULE_SERVICE_PROVIDER,
        CONFIG_ALL_SERVICE_PROVIDER,
        REPORTS_SERVICE_PROVIDER_IMPL,
        EXTERNAL_SYSTEMS_SERVICE_PROVIDER,
        GEOID_SERVICE_PROVIDER,
        REPORT_PROBLEM_SERVICE_PROVIDER,
        USER_ANALYTICS_SERVICE_PROVIDER,
        COLLECTS_ANALYTICS_SERVICE_PROVIDER,
        REMINDER_ANALYTICS_SERVICE_PROVIDER,
        {
            provide: 'HTTP_CLIENT',
            useClass: HttpClient,
        },
        {
            provide: 'BCITI_CONTEXT_SERVICE',
            useClass: BcitiContextServiceImpl,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'YYYY/MM/DD',
                },
                display: {
                    dateInput: 'YYYY/MM/DD',
                    monthYearLabel: 'MM YYYY',
                    dateA11yLabel: 'YYYY/MM/DD',
                    monthYearA11yLabel: 'MM YYYY',
                },
            },
        },
        {
            provide: MAT_COLOR_FORMATS,
            useValue: NGX_MAT_COLOR_FORMATS,
        },
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'fr-FR',
        },
        {
            provide: DateAdapter,
            useClass: CustomDateAdapter,
        },
        {
            provide: NgxMatDateAdapter,
            useClass: NgxMatMomentAdapter,
            deps: [MAT_DATE_LOCALE, NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        { provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
        TranslateStore,
        DatePipe,
        ShortDatePipe,
        CdkColumnDef,
    ],
})
export class GlobalModule {}
